// NEW_FILE_JUSTIFIED: Single field tile inside a section in the builder canvas
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import { getFieldMeta } from '@/lib/insurance/fieldTypeRegistry';
import type { IFieldCardProps } from '@/@types/insuranceBuilder';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export function FieldCard({
  field,
  sectionKey,
  isSelected,
  dispatch,
  isAiExtracted,
}: IFieldCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.fieldKey,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const meta = getFieldMeta(field.fieldType);
  const Icon = meta.icon;

  function handleSelect(e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest('[data-no-select]')) return; // AS_JUSTIFIED: e.target is always Element here; closest() requires Element
    e.stopPropagation();
    dispatch({
      type: 'SELECT',
      selection: { kind: 'field', sectionKey, fieldKey: field.fieldKey },
    });
  }
  function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    dispatch({ type: 'DELETE_FIELD', sectionKey, fieldKey: field.fieldKey });
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={handleSelect}
      className={cn(
        'border rounded-md p-3 bg-background cursor-pointer transition-all hover:border-primary/40',
        isSelected && 'ring-2 ring-primary ring-offset-1',
        field.fullWidth && 'col-span-full'
      )}
    >
      <div className="flex items-center gap-2">
        <button
          {...attributes}
          {...listeners}
          aria-label={`Drag field ${field.label}`}
          data-no-select
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        <span className="text-sm font-medium truncate flex-1">
          {field.label}
          {field.required && <span className="text-destructive ml-1">*</span>}
        </span>
        <span className="text-xs text-muted-foreground capitalize">{meta.label}</span>
        {isAiExtracted && (
          <Badge
            variant="secondary"
            className="bg-primary/15 text-primary border border-primary/30 text-xs"
          >
            AI extracted
          </Badge>
        )}
        <Button
          data-no-select
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={handleDelete}
          aria-label={`Delete field ${field.label}`}
        >
          <Trash2 className="w-3.5 h-3.5 text-destructive" />
        </Button>
      </div>
      {field.helpText && (
        <p className="text-xs text-muted-foreground mt-1 ml-6">{field.helpText}</p>
      )}
    </div>
  );
}
